import React, { useState,useEffect } from 'react';
import { Modal } from '@mantine/core';
import { CustomButton } from 'src/components/common/Buttons/CustomButton';
import { ThumbUp, ThumbDown } from '@mui/icons-material';
import FeedbackModal from './FeedbackModal';
import { generateSuggestedQuestion, sendSuggestedQuestionEmail } from 'src/api/publicChat';
import FloatingInput from '../Inputs/FloatingInput';

interface EmailModalProps {
  opened: boolean;
  onClose: () => void;
  zepSessionId: string;
  name:string;
}


const EmailModal: React.FC<EmailModalProps> = ({ opened, onClose, zepSessionId, name}) => {
  const [email, setEmail] = useState('');
  const [feedbackModal, setFeedbackModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userName,setUserName] = useState(name)
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedThumb, setSelectedThumb] = useState<'up' | 'down' | null>(null);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  // Handle window resize for responsive icons
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Focus email input when modal opens
  useEffect(() => {
    if (opened) {
      // Small delay to ensure modal is fully rendered
      const timer = setTimeout(() => {
        // Focus the email input using its label as selector
        const emailInput = document.querySelector('input[aria-label="Email Address"]') as HTMLInputElement;
        if (emailInput) {
          emailInput.focus();
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [opened]);

  useEffect(() => {
    resetAllStates()
  }, [opened]);

  const handleFeedbackClose = () => {
    setFeedbackModal(false);
  }

  const resetAllStates = async () => {
    setEmail('')
    setError('')
    setLoading(false)
    setSuccessMessage('')
    setSelectedThumb(null)
  }

  const handleFeedbackClick = ()=>{
    //Open FeedbackModal
    setFeedbackModal(true);
    onClose();    //Close Email Modal
    // resetAllStates();
  }

  // useEffect(() => {
  //   const generateSuggestedQuestionForEmail = async () => {
  //     const response = await generateSuggestedQuestion(zepSessionId);
  
  //     if (response) {
  //       console.log("✅ Suggested question generated successfully");
  //     } else {
  //       console.error("❌ Failed to generate suggested question");
  //     }
  //   };
  //   if (opened){
  //     generateSuggestedQuestionForEmail();
  //   }
  // }, [opened, zepSessionId]);

  // Email validation (copied from FeedbackModal for consistency)
  const validateEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleSend = async () => {
    setError('');
    setSuccessMessage('');
    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }
    setLoading(true);
    try {
      const result = await sendSuggestedQuestionEmail(zepSessionId, email,name);
      if (!result) {
        setError('Failed to send email. Please try again.');
      } else {
        setSuccessMessage('Email sent successfully!');
      }
    } catch (e) {
      setError('Failed to send email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleThumbClick = (thumbType: 'up' | 'down') => {
    setSelectedThumb(thumbType);
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        title="Was MYAi helpful for your learning?"
        size='lg'
        centered
        trapFocus={false}
        styles={{
          title: {
            fontWeight: 'bold',
            fontSize: '1.5rem',
            color: '#29748F',
            textAlign: 'center',
            flexGrow: 1
          },
          header: {
            paddingTop: '20px'
          },
          root: {
            borderRadius: '16px'
          },
          body: {
            padding: '20px 30px'
          }
        }}
      >

<div className='flex flex-col justify-center gap-y-4'>
        <div className="flex justify-evenly items-center p-2 sm:p-4 rounded-lg w-full max-w-md mx-auto">
          <div 
            onClick={() => handleThumbClick('up')}
            className={`cursor-pointer p-2 sm:p-3 rounded-full transition-all duration-200 ${
              selectedThumb === 'up' 
                ? 'bg-green-100 shadow-lg scale-110' 
                : 'hover:bg-green-50 hover:scale-105'
            }`}
          >
            <ThumbUp 
              style={{ 
                fontSize: windowWidth < 640 ? 35 : 50,
                color: selectedThumb === 'up' ? '#2e7d32' : '#66bb6a'
              }} 
            />
          </div>
          <div 
            onClick={() => handleThumbClick('down')}
            className={`cursor-pointer p-2 sm:p-3 rounded-full transition-all duration-200 ${
              selectedThumb === 'down' 
                ? 'bg-red-100 shadow-lg scale-110' 
                : 'hover:bg-red-50 hover:scale-105'
            }`}
          >
            <ThumbDown 
              style={{ 
                fontSize: windowWidth < 640 ? 35 : 50,
                color: selectedThumb === 'down' ? '#c62828' : '#ef5350'
              }} 
            />
          </div>
        </div>
          <h3 className='font-bold text-center text-sm sm:text-base'>
            Please provide a valid e-mail address to get practice questions.
          </h3>
          
        <div className='flex flex-col sm:flex-row gap-y-2 sm:gap-y-0 sm:gap-x-3'>
          <FloatingInput
            value={email}
            setValue={setEmail}
            label="Email Address"
            maxLength={30}
            width="w-full"
            placeholder="Enter your email address"
            disableFloatingLabel={true}
          />
          <CustomButton
            onClick={handleSend}
            variant='default'
            size='lg'
            width="w-full sm:w-[120px]"
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
                <span className="text-white text-sm">Sending...</span>
              </div>
            ) : (
              <span className="text-white text-sm">Send</span>
            )}
          </CustomButton>
        </div>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mt-2">
            {error}
          </div>
        )}
        {successMessage && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm mt-2">
            {successMessage}
          </div>
        )}

        <div className='flex flex-col justify-center gap-y-3 sm:gap-y-4'>
          <CustomButton
            variant='default'
            size='lg'
            width="w-full"
            disabled={loading}
            textClass='text-base sm:text-[1.1rem]'
            onClick={()=> window.open(process.env.REACT_APP_FRONTEND_URL + '/auth/signup', '_blank')}
          >
            Claim Your Free Access
          </CustomButton>
          <button
            onClick={()=>handleFeedbackClick()}
            disabled={loading}
            className="text-primary hover:text-primary-dark underline text-xs sm:text-sm text-center cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Got feedback? We'd love to hear it
          </button>
        </div>
        </div>
      </Modal>
      <FeedbackModal opened={feedbackModal} onClose={handleFeedbackClose}  previousEmail={email} name={name} conversation_id={zepSessionId}/>
    </>
  );
};

export default EmailModal;